import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  clockIn,
  clockOut,
  findEmployeeByDni,
  getCurrentBranchId,
  getCurrentSessionToken,
  getLatestSession,
  getTodaySessions,
  getTodayYmd,
  summarizeSessions,
} from "../api/terminalAttendanceApi";
import { useEmpresa } from "../../../app/providers/EmpresaProvider";

function formatMinutes(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${m}m`;
}

function sessionWorkedMinutes(session) {
  if (!session?.clockInAt || !session?.clockOutAt) return 0;
  const inMs = new Date(session.clockInAt).getTime();
  const outMs = new Date(session.clockOutAt).getTime();
  if (!Number.isFinite(inMs) || !Number.isFinite(outMs) || outMs < inMs) return 0;
  return Math.round((outMs - inMs) / 60000);
}

function formatDateTime(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return "-";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function AttendanceTerminalPanel({
  onSummaryChange = null,
  onEmployeeSnapshotChange = null,
  showEmployeeDetails = true,
  showResetButton = true,
  cardSx = {},
}) {
  const navigate = useNavigate();
  const { sucursalId } = useEmpresa();

  const [dni, setDni] = useState("");
  const [employeeId, setEmployeeId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [latestSessionStatus, setLatestSessionStatus] = useState(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const currentBranchId = useMemo(() => {
    return sucursalId || getCurrentBranchId() || "";
  }, [sucursalId]);

  const ymd = useMemo(() => getTodayYmd(), []);
  const summary = useMemo(() => summarizeSessions(sessions), [sessions]);
  const nextAction = latestSessionStatus === "OPEN" ? "OUT" : "IN";

  useEffect(() => {
    if (!currentBranchId) {
      navigate("/select-branch", {
        replace: true,
        state: { next: "/attendance" },
      });
    }
  }, [currentBranchId, navigate]);

  function emitSummary(employee, nextSessions, nextLatestStatus) {
    if (typeof onSummaryChange !== "function") return;
    const nextSummary = summarizeSessions(nextSessions);
    onSummaryChange({
      employeeId: Number(employee?.employeeId || 0),
      fullName: String(employee?.fullName || "").trim(),
      dni: String(employee?.dni || "").trim(),
      workedMinutes: Number(nextSummary.workedMinutes || 0),
      latestStatus: nextLatestStatus || null,
      updatedAt: new Date().toISOString(),
    });
  }

  function emitEmployeeSnapshot(employee, nextSessions, nextLatestStatus) {
    if (typeof onEmployeeSnapshotChange !== "function") return;
    const nextSummary = summarizeSessions(nextSessions);
    onEmployeeSnapshotChange({
      employeeId: Number(employee?.employeeId || 0),
      fullName: String(employee?.fullName || "").trim(),
      dni: String(employee?.dni || "").trim(),
      sessions: Array.isArray(nextSessions) ? nextSessions : [],
      latestStatus: nextLatestStatus || null,
      workedMinutes: Number(nextSummary.workedMinutes || 0),
      ymd,
      updatedAt: new Date().toISOString(),
    });
  }

  async function fetchSessionSnapshot(nextToken, nextEmployeeId) {
    const [todayResponse, latestSession] = await Promise.all([
      getTodaySessions({
        employeeId: nextEmployeeId,
        date: ymd,
        branchId: currentBranchId,
      }),
      getLatestSession({
        employeeId: nextEmployeeId,
        branchId: currentBranchId,
      }),
    ]);

    const nextSessions = Array.isArray(todayResponse?.data) ? todayResponse.data : [];
    const nextLatestStatus = latestSession?.status ?? null;
    return { nextSessions, nextLatestStatus };
  }

  async function refreshSessions(nextToken, employee) {
    const nextEmployeeId = Number(employee?.employeeId || 0);
    const { nextSessions, nextLatestStatus } = await fetchSessionSnapshot(
      nextToken,
      nextEmployeeId
    );
    setSessions(nextSessions);
    setLatestSessionStatus(nextLatestStatus);
    emitSummary(employee, nextSessions, nextLatestStatus);
    emitEmployeeSnapshot(employee, nextSessions, nextLatestStatus);
  }

  function clearAttendanceState() {
    setEmployeeId(null);
    setSessions([]);
    setLatestSessionStatus(null);
  }

  async function handleSubmitByDni(e) {
    e.preventDefault();
    setWorking(true);
    setError("");
    setSuccess("");

    try {
      const nextToken = getCurrentSessionToken();
      if (!nextToken) {
        throw new Error("Sesion expirada. Volve a iniciar sesion para marcar asistencia");
      }

      if (!currentBranchId) {
        throw new Error("No hay una sucursal seleccionada para esta terminal");
      }

      const employee = await findEmployeeByDni({ dni, token: nextToken });
      const nextEmployeeId = employee.employeeId;
      setEmployeeId(nextEmployeeId);

      const initialSnapshot = await fetchSessionSnapshot(nextToken, nextEmployeeId);
      const actionToExecute = initialSnapshot.nextLatestStatus === "OPEN" ? "OUT" : "IN";
      let markResponse = null;

      if (actionToExecute === "IN") {
        markResponse = await clockIn({
          employeeId: nextEmployeeId,
          branchId: currentBranchId,
        });
      } else {
        markResponse = await clockOut({
          employeeId: nextEmployeeId,
          branchId: currentBranchId,
        });
      }

      await refreshSessions(nextToken, employee);

      const fullName = employee.fullName || `empleado #${nextEmployeeId}`;
      const markTime = formatDateTime(markResponse?.timestamp || new Date().toISOString());

      setSuccess(
        actionToExecute === "IN"
          ? `Entrada registrada para ${fullName} a las ${markTime}.`
          : `Salida registrada para ${fullName} a las ${markTime}.`
      );

      setDni("");
    } catch (err) {
      const message = String(err?.message || "").toLowerCase();
        
      if (err?.status === 401) {
        setError("Tu sesion no es valida para marcar asistencia.");
      } else if (err?.status === 403) {
        setError("Este usuario no tiene permisos para este tipo de marcacion.");
      } else if (message.includes("no existe un empleado")) {
        setError("No existe un empleado con ese DNI.");
      } else if (message.includes("no está activo") || message.includes("no esta activo")) {
        setError("El empleado no está activo.");
      } else if (message.includes("no pertenece a la sucursal")) {
        setError("El empleado no está habilitado para marcar en esta sucursal.");
      } else if (
        message.includes("no hay sesión abierta") ||
        message.includes("no hay sesion abierta")
      ) {
        setError("No hay una sesión abierta en esta sucursal para registrar salida.");
      } else if (message.includes("salida no puede ser anterior")) {
        setError("La salida no puede ser anterior a la entrada.");
      } else if (message.includes("no hay una sucursal seleccionada")) {
        setError("Primero tenés que seleccionar una sucursal para esta terminal.");
      } else if (err?.status === 409) {
        setError("Ya existe una sesion abierta para este empleado.");
      } else if (err?.status === 400) {
        setError(err?.message || "Datos invalidos para registrar asistencia.");
      } else {
        setError(err?.message || "No se pudo registrar la asistencia");
      }
    } finally {
      setWorking(false);
    }
  }

  function handleReset() {
    setDni("");
    clearAttendanceState();
    setError("");
    setSuccess("");

    if (typeof onSummaryChange === "function") {
      onSummaryChange(null);
    }

    if (typeof onEmployeeSnapshotChange === "function") {
      onEmployeeSnapshotChange(null);
    }
  }

  if (!currentBranchId) return null;

  return (
    <Card sx={{ width: "100%", borderRadius: 3, ...cardSx }}>
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="h6" fontWeight={700}>
            Terminal de Asistencia
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmitByDni}
            autoComplete="off"
          >
            <Stack spacing={2}>
              <TextField
                label="DNI de empleado"
                type="text"
                name="terminal_attendance_dni_no_autocomplete"
                autoComplete="new-password"
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  maxLength: 12,
                  autoComplete: "new-password",
                  autoCorrect: "off",
                  autoCapitalize: "none",
                  spellCheck: "false",
                }}
                value={dni}
                onChange={(e) => setDni(e.target.value.replace(/\D/g, ""))}
                required
                fullWidth
              />

              <Button type="submit" variant="contained" disabled={working}>
                {working ? <CircularProgress size={20} color="inherit" /> : "Marcar asistencia"}
              </Button>
            </Stack>
          </Box>

          {showEmployeeDetails && employeeId ? (
            <Stack spacing={2}>
              <Typography variant="body1">Empleado seleccionado</Typography>

              <Typography variant="body1" fontWeight={600}>
                Próxima acción: {nextAction === "IN" ? "Marcar entrada" : "Marcar salida"}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Horas acumuladas hoy: {formatMinutes(summary.workedMinutes)}
              </Typography>

              <Stack spacing={0.8} sx={{ bgcolor: "#f7f8fb", borderRadius: 2, p: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={700}>
                  Ingresos y egresos de hoy
                </Typography>

                {sessions.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Aun no hay marcaciones para este empleado hoy.
                  </Typography>
                ) : (
                  sessions.map((session) => (
                    <Typography key={session.sessionId} variant="body2" color="text.secondary">
                      #{session.sessionId} | Ingreso: {formatDateTime(session.clockInAt)} | Egreso:{" "}
                      {formatDateTime(session.clockOutAt)} | Estado: {session.status} | Horas:{" "}
                      {formatMinutes(sessionWorkedMinutes(session))}
                    </Typography>
                  ))
                )}
              </Stack>

              {showResetButton ? (
                <Button variant="outlined" onClick={handleReset} disabled={working}>
                  Limpiar
                </Button>
              ) : null}
            </Stack>
          ) : null}

          {error ? <Alert severity="error">{error}</Alert> : null}
          {success ? <Alert severity="success">{success}</Alert> : null}
        </Stack>
      </CardContent>
    </Card>
  );
}